import { FC, SyntheticEvent, useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Checkbox, Counter, Select, Typography } from "theme";
import { ArrowLeft } from "theme/icons";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid2,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { Category, Equipment, getCategoryName } from "../../../../api/hooks/useEquipment/type.ts";

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => {
  return {
    "&.MuiFormControlLabel-root": {
      margin: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      columnGap: theme.spacing(2.5),
    },
  };
});

const StyledImage = styled("img")(({ theme }) => {
  return {
    height: "88px",
    width: "88px",
    objectFit: "contain",
    backgroundColor: theme.palette.common.white,
  };
});

function generateRange(rangeEnd: number): number[] {
  const result: number[] = [];
  for (let i = 1; i <= rangeEnd; i += 0.5) {
    result.push(i);
  }
  return result;
}

const LabelWrapper: FC<{
  option: Equipment;
  isRangeSelectionOpen: boolean;
  onChange: (id: string, count: number, range: number) => void;
  checked: boolean;
}> = ({ option, isRangeSelectionOpen, onChange, checked }) => {
  const { palette } = useTheme();
  const [searchParams] = useSearchParams();
  const defaultValues = useMemo(() => {
    const equipmentData = localStorage.getItem("equipment");

    if (equipmentData) {
      const equipment = JSON.parse(equipmentData) as SelectedCheckboxValues[];

      const selectedEl = equipment.find((el) => el.id === String(option.id));

      if (selectedEl) {
        return {
          c: selectedEl.count,
          r: selectedEl.range,
        };
      }
    }

    return {
      c: 1,
      r: null,
    };
  }, [option.id]);

  const matches = useMediaQuery("(max-width:390px)");
  const [count, setCount] = useState<number | null>(defaultValues.c);

  const selectValues = generateRange(Number(searchParams.get("r") ?? 1));
  const [selectedOptionRange, setSelectedOptionRange] = useState<number>(
    defaultValues.r ?? selectValues[selectValues.length - 1],
  );

  return (
    <Grid2 my={1.25} container columnGap={1.5} flexWrap="nowrap">
      <Box>
        <StyledImage src={option.imageUrl} />
      </Box>
      <Box>
        <Typography variant="body1" weight="regular" textTransform="uppercase">
          {option.title}
        </Typography>

        {isRangeSelectionOpen && (
          <Box mt={1.25} maxWidth={100}>
            <FormControl fullWidth>
              <Typography variant="body2">Кількість годин</Typography>
              <Select
                MenuProps={{
                  sx: {
                    "& .MuiPaper-root": {
                      background: palette.primary.main,
                    },
                  },
                }}
                value={selectedOptionRange}
                onChange={(e) => {
                  setSelectedOptionRange(Number(e.target.value));
                  onChange(String(option.id), Number(count), Number(e.target.value));
                }}
              >
                {selectValues.map((el) => {
                  return (
                    <MenuItem key={el} value={el}>
                      {el} год
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        )}

        <Grid2 container mt={2.5} alignItems="center" columnGap={matches ? 1 : 5} rowGap={2}>
          <Typography height={34} variant="body2" weight="semiBold" textTransform="uppercase">
            {option.pricePerUnit} {option.isFixedPrice ? "грн" : "грн/год"}
          </Typography>

          {checked && (
            <Counter
              value={count}
              onChange={(v) => {
                setCount(v);
                onChange(String(option.id), Number(v), selectedOptionRange);
              }}
              maxValue={option.availableCount}
            />
          )}
        </Grid2>
      </Box>
    </Grid2>
  );
};

const StyledAccordion = styled(Accordion)(({ theme }) => {
  return {
    "&.MuiAccordion-root": {
      backgroundColor: "transparent",
      boxShadow: "none",
      margin: 0,

      "&.Mui-expanded": {
        marginTop: 0,
      },

      "&::before": {
        display: "none",
      },

      "& .MuiAccordionSummary-root, & .MuiAccordionDetails-root": {
        padding: 0,
      },

      "& .MuiAccordionSummary-root": {
        minHeight: 0,
        justifyContent: "start",
        columnGap: theme.spacing(0.5),

        "& .MuiAccordionSummary-content": {
          flexGrow: 0,
        },
      },

      "& .MuiCollapse-root": {
        marginTop: theme.spacing(2.5),
      },

      "& .MuiAccordionSummary-root .MuiAccordionSummary-content": {
        margin: 0,

        "& .MuiTypography-root": {
          margin: 0,
        },
      },
    },
  };
});

const StyledAdditionalSetting = styled(FormControlLabel)(({ theme }) => {
  return {
    "&.MuiFormControlLabel-root": {
      margin: 0,
      display: "flex",
      alignItems: "center",
      columnGap: theme.spacing(0.5),
    },
  };
});

export type SelectedCheckboxValues = {
  id: string;
  count: number;
  range: number;
  pricePerUnit: number;
};

export type EquipmentSelectionCheckboxGroupProps = {
  name: string;
  options: {
    categoryName: Category;
    equipments: Equipment[];
  }[];
  onChange: (value: SelectedCheckboxValues[]) => void;
  value: SelectedCheckboxValues[];
};

const EquipmentSelectionCheckboxGroup: FC<EquipmentSelectionCheckboxGroupProps> = ({
  onChange,
  value,
  options,
  name,
}) => {
  const [searchParams] = useSearchParams();
  const [isSettingOpen, setIsSettingOpen] = useState<boolean>(false);

  const handleChange = useCallback(
    (e: SyntheticEvent, checked: boolean, price: number, isFixedPrice: boolean) => {
      const changedValue = (e.currentTarget as HTMLInputElement).value;
      const changedEl = {
        id: changedValue,
        count: 1,
        range: Number(searchParams.get("r") ?? 1),
        pricePerUnit: price,
        isFixedPrice,
      };

      onChange(checked ? [...value, changedEl] : value.filter((i) => i.id !== changedValue));
    },
    [searchParams, onChange, value],
  );

  const handleOptionsChange = useCallback(
    (id: string, count: number, range: number) => {
      onChange(
        ...[
          value.map((el) => {
            if (el.id === id) {
              return {
                ...el,
                count,
                range,
              };
            }

            return el;
          }),
        ],
      );
    },
    [onChange, value],
  );

  return (
    <FormControl fullWidth>
      <FormGroup>
        {options.map((option) => {
          return (
            <StyledAccordion key={option.categoryName} defaultExpanded>
              <AccordionSummary
                expandIcon={
                  <ArrowLeft
                    style={{
                      height: "10px",
                    }}
                  />
                }
              >
                <Typography lineHeight={0} variant="h2" weight="semiBold" textTransform="uppercase">
                  {getCategoryName(option.categoryName)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <StyledAdditionalSetting
                    checked={isSettingOpen}
                    value={isSettingOpen}
                    onChange={() => {
                      setIsSettingOpen((prevState) => !prevState);
                    }}
                    control={<Checkbox />}
                    label={<Typography variant="body1">Детальний вибір часу</Typography>}
                  />
                </Box>
                {option.equipments.map((eq) => {
                  const checked = value.some((el) => el.id === String(eq.id));
                  const disabled = eq.availableCount === 0;

                  return (
                    <StyledFormControlLabel
                      key={eq.id}
                      labelPlacement="start"
                      name={name}
                      value={String(eq.id)}
                      control={<Checkbox />}
                      disabled={disabled}
                      label={
                        <LabelWrapper
                          onChange={handleOptionsChange}
                          isRangeSelectionOpen={isSettingOpen}
                          option={eq}
                          checked={checked}
                        />
                      }
                      checked={checked}
                      onChange={(e, checked) =>
                        handleChange(e, checked, eq.pricePerUnit, eq.isFixedPrice)
                      }
                    />
                  );
                })}
              </AccordionDetails>
            </StyledAccordion>
          );
        })}
      </FormGroup>
    </FormControl>
  );
};

export default EquipmentSelectionCheckboxGroup;
